import { addDays, parseISO } from 'date-fns';

/**
 * Get current time in a specific timezone using Intl.DateTimeFormat
 * This allows us to compare times accurately across different timezones
 * @param timezone - IANA timezone string (e.g., 'Australia/Brisbane', 'America/New_York')
 * @returns Date object representing the current time as if it were in the specified timezone
 */
export const getCurrentTimeInTimezone = (timezone: string): Date => {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
    });
    const parts = formatter.formatToParts(now);
    const get = (type: string) => parts.find(p => p.type === type)?.value || '0';

    return new Date(`${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}:${get('second')}`);
};

/**
 * Get default schedule date (2 days from now, broadcasts ONLY)
 */
export const getDefaultScheduleDate = (): Date => {
    return addDays(new Date(), 2);
};

/**
 * Get default schedule time (1 hour from now, rounded to nearest 15-minute interval)
 * Returns time in minutes since midnight (0-1439)
 */
export const getDefaultScheduleTime = (): number => {
    const now = new Date();
    const oneHourFromNow = new Date(now.getTime() + 60 * 60 * 1000);
    const totalMinutes = oneHourFromNow.getHours() * 60 + oneHourFromNow.getMinutes();
    // Round to nearest 15-minute interval
    return Math.round(totalMinutes / 15) * 15;
};

/**
 * Get default timezone (user's local timezone)
 */
export const getDefaultTimezone = (): string => {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * Validates that a scheduled time is at least 10 minutes in the future
 * @param dateString - ISO date string (YYYY-MM-DD)
 * @param timeInMinutes - Time in minutes since midnight (0-1439)
 * @param timezone - Optional IANA timezone string.
 * @returns Object with isValid boolean and optional error message
 */
export const isScheduledTimeValid = (
    dateString: string,
    timeInMinutes: number,
    timezone?: string
): { isValid: boolean; error?: string } => {
    try {
        const selectedDate = parseISO(dateString);
        const hours = Math.floor(timeInMinutes / 60);
        const minutes = timeInMinutes % 60;

        // Build datetime string: YYYY-MM-DDTHH:mm:00
        const year = selectedDate.getFullYear();
        const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
        const day = String(selectedDate.getDate()).padStart(2, '0');
        const hoursStr = String(hours).padStart(2, '0');
        const minutesStr = String(minutes).padStart(2, '0');
        const dateTimeString = `${year}-${month}-${day}T${hoursStr}:${minutesStr}:00`;

        const selectedDateTime = new Date(dateTimeString);
        // Use timezone-aware current time if timezone is provided, otherwise use browser's local time
        const now = timezone ? getCurrentTimeInTimezone(timezone) : new Date();
        const diffInMs = selectedDateTime.getTime() - now.getTime();

        // Check if scheduled time is in the past
        if (selectedDateTime <= now) {
            return { isValid: false, error: 'Broadcasts cannot be scheduled in the past!' };
        }

        // Require at least 10 minutes (600,000 ms) in the future
        if (diffInMs < 10 * 60 * 1000) {
            return { isValid: false, error: 'Please schedule the broadcast at least 10 minutes in the future!' };
        }

        return { isValid: true };
    } catch {
        // If parsing fails, allow it (other validations will catch it)
        return { isValid: true };
    }
};

/**
 * Validates schedule time for Date objects (used by ScheduleBroadcastModal)
 * @param date - Selected date
 * @param timeInMinutes - Time in minutes since midnight (0-1439)
 * @param timezone - Optional IANA timezone string for timezone-aware validation
 * @returns Object with isValid boolean and optional error message
 */
export const validateScheduleTime = (
    date: Date,
    timeInMinutes: number,
    timezone?: string
): { isValid: boolean; error?: string } => {
    if (!date) {
        return { isValid: false, error: 'Please select a date' };
    }

    // Convert to ISO string and use the main validation function
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateString = `${year}-${month}-${day}`;

    return isScheduledTimeValid(dateString, timeInMinutes, timezone);
};
